import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Descriptions,
  Alert,
  Input,
  Checkbox,
  Spin,
  Typography,
  Tag,
  message,
} from "antd";
import {
  getPreLeadClientPreview,
  createPreLeadClient,
} from "../Actions/preLeadsAction";
import { formatPayloadValue } from "../Utils/preLeadPayload";

const { Text } = Typography;

// Fields worth showing on the confirm screen. The preview returns more than
// this, but these are the ones a person can sanity-check at a glance.
const PREVIEW_FIELDS = [
  ["name", "Name"],
  ["mobile_number", "Mobile"],
  ["city", "City"],
  ["area", "Area"],
  ["property_type", "Property type"],
  ["client_type", "Client type"],
  ["reference", "Reference"],
];

/**
 * Confirm screen for turning a pre-lead into a Client. Always previews first
 * (which writes nothing) so staff can see whether this creates a new client or
 * matches an existing one, and can correct the name before saving.
 */
const PreLeadClientModal = ({ open, onClose, preLead, onResolved }) => {
  const dispatch = useDispatch();
  const { clientPreview, isClientPreviewLoading, isCreateClientLoading } =
    useSelector((state) => state.preLeads);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [enrich, setEnrich] = useState(false);

  useEffect(() => {
    if (open && preLead?.id) {
      setEnrich(false);
      dispatch(
        getPreLeadClientPreview(preLead.id, null, (error) =>
          message.error(error || "Could not load the client preview"),
        ),
      );
    }
  }, [dispatch, open, preLead?.id]);

  useEffect(() => {
    setName(clientPreview?.mapped?.name || "");
    setMobile(clientPreview?.mapped?.mobile_number || "");
  }, [clientPreview]);

  const existing = clientPreview?.existing_client;
  const wouldCreate = clientPreview?.would_create;
  const phoneUsable = clientPreview?.phone_usable;
  const fillable = clientPreview?.fillable_on_existing || {};
  const hasFillable = Object.keys(fillable).length > 0;

  const handleSubmit = () => {
    const overrides = {};
    if (name.trim()) overrides.name = name.trim();
    // Only send a mobile override when the payload had no usable number,
    // otherwise the backend's own normalized value is left alone.
    if (!phoneUsable && mobile.trim()) overrides.mobile_number = mobile.trim();

    dispatch(
      createPreLeadClient(
        preLead.id,
        { client: overrides, enrich },
        (data) => {
          message.success(
            data?.created ? "Client created" : "Matched an existing client",
          );
          onResolved && onResolved(data);
          onClose();
        },
        (error) => message.error(error || "Could not save the client"),
      ),
    );
  };

  return (
    <Modal
      title="Create client from pre-lead"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={wouldCreate ? "Create client" : "Link existing client"}
      okButtonProps={{
        loading: isCreateClientLoading,
        disabled: isClientPreviewLoading || (!phoneUsable && !mobile.trim()),
      }}
      width={640}
      destroyOnClose
    >
      <Spin spinning={isClientPreviewLoading}>
        {!phoneUsable && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="No usable phone number in this payload"
            description="A number must reduce to exactly 10 digits. Enter one below to continue."
          />
        )}

        {existing && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="We already have this person"
            description={`Matched on phone number: ${existing.name || `#${existing.id}`}. No duplicate will be created.`}
          />
        )}

        <div style={{ marginBottom: 12 }}>
          <Text strong>Name</Text>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Client name"
          />
        </div>

        {!phoneUsable && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Mobile number</Text>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="10-digit mobile number"
            />
          </div>
        )}

        <Descriptions size="small" column={1} bordered>
          {PREVIEW_FIELDS.map(([key, label]) => (
            <Descriptions.Item label={label} key={key}>
              {formatPayloadValue(clientPreview?.mapped?.[key])}
            </Descriptions.Item>
          ))}
        </Descriptions>

        {clientPreview?.mapped?.email_rejected && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Portal email not stored"
            description={
              <>
                <Tag>{clientPreview.mapped.email_rejected}</Tag> is a relay or
                account address, not the buyer's. It is recorded in the client
                note instead of the email field.
              </>
            }
          />
        )}

        {existing && hasFillable && (
          <Checkbox
            checked={enrich}
            onChange={(e) => setEnrich(e.target.checked)}
            style={{ marginTop: 12 }}
          >
            Fill in the {Object.keys(fillable).length} detail
            {Object.keys(fillable).length === 1 ? "" : "s"} currently blank on
            this client ({Object.keys(fillable).join(", ")}). Existing values are
            never overwritten.
          </Checkbox>
        )}
      </Spin>
    </Modal>
  );
};

export default PreLeadClientModal;
